import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';

export interface DialogAction {
  label: string;
  action: string | (() => void);
  color?: 'primary' | 'accent' | 'warn';
  style?: 'basic' | 'primary' | 'accent' | 'warn'; // Legacy compatibility
  icon?: string;
  disabled?: boolean | (() => boolean);
  variant?: 'raised' | 'stroked' | 'flat';
}

/**
 * Reusable dialog actions component that provides consistent button
 * layouts and styling across all dialog components.
 */
@Component({
  selector: 'lib-dialog-actions',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatDialogModule],
  template: `
    <mat-dialog-actions align="end" class="dialog-actions">
      @for (action of actions; track $index) {
        @if (getVariant(action) === 'raised') {
          <button mat-raised-button
                  [color]="getColor(action)"
                  [disabled]="isDisabled(action)"
                  (click)="onActionClick(action)">
            @if (action.icon) {
              <mat-icon>{{ action.icon }}</mat-icon>
            }
            {{ action.label }}
          </button>
        } @else if (getVariant(action) === 'stroked') {
          <button mat-stroked-button
                  [color]="getColor(action)"
                  [disabled]="isDisabled(action)"
                  (click)="onActionClick(action)">
            @if (action.icon) {
              <mat-icon>{{ action.icon }}</mat-icon>
            }
            {{ action.label }}
          </button>
        } @else {
          <button mat-button
                  [color]="getColor(action)"
                  [disabled]="isDisabled(action)"
                  (click)="onActionClick(action)">
            @if (action.icon) {
              <mat-icon>{{ action.icon }}</mat-icon>
            }
            {{ action.label }}
          </button>
        }
      }
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-actions {
      gap: var(--spacing-xs);
      padding: var(--spacing-md) var(--spacing-lg);

      button {
        min-width: 80px;

        mat-icon {
          margin-right: 8px;
          margin-left: -4px;
        }
      }
    }
  `]
})
export class DialogActionsComponent {
  @Input() actions: DialogAction[] = [];
  @Output() actionClick = new EventEmitter<string>();

  onActionClick(action: DialogAction) {
    if (typeof action.action === 'function') {
      action.action();
    } else {
      this.actionClick.emit(action.action);
    }
  }

  getVariant(action: DialogAction): string {
    if (action.variant) return action.variant;
    if (action.style === 'primary') return 'raised';
    return 'flat';
  }

  getColor(action: DialogAction): string {
    if (action.color) return action.color;
    if (action.style === 'primary') return 'primary';
    return '';
  }

  isDisabled(action: DialogAction): boolean {
    if (typeof action.disabled === 'function') {
      return action.disabled();
    }
    return action.disabled || false;
  }

  /**
   * Helper method to create standard save/cancel actions
   */
  static createSaveCancelActions(saveDisabled = false): DialogAction[] {
    return [
      {
        label: 'Cancel',
        action: 'cancel',
        variant: 'flat'
      },
      {
        label: 'Save',
        action: 'save',
        color: 'primary',
        variant: 'raised',
        icon: 'save',
        disabled: saveDisabled
      }
    ];
  }

  /**
   * Helper method to create standard create/cancel actions
   */
  static createCreateCancelActions(createDisabled = false): DialogAction[] {
    return [
      {
        label: 'Cancel',
        action: 'cancel',
        variant: 'flat'
      },
      {
        label: 'Create',
        action: 'create',
        color: 'primary',
        variant: 'raised',
        icon: 'add',
        disabled: createDisabled
      }
    ];
  }
}
